import { FootnoteReferenceV2 } from '@oneaudi/feature-app-utils';
import { Footnote } from '@oneaudi/footnote-service/dist/cjs/types/footnotes-response.type';
import { Text } from '@oneaudi/unified-web-components';
import { useAtomValue } from 'jotai';
import MonthlyRateFootnote from '~/app/compositions/content/calculation-info/components/monthly-rate-footnote';
import { useAppServices } from '~/app/contexts/app-context';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig, VATConfig } from '~/app/market-config';
import { FootnoteReferenceNgc } from '~/components/footnotes/footnote-reference';
import { useFinancing } from '~/graphql/hooks/use-financing';
import { PriceOrError } from '~/graphql/mapper';
import { CurrencyFormatterDisplayContext, useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import { state } from '~/storage';

export function Prices({
	id,
	totalPrice,
	pr7,
	monthlyRateOverride,
	monthlyRateDialog,
	monthlyRateDialogReference,
	displayContext,
	vatConfig,
	displayMode = 'rows',
	priceDisclaimer,
	size = '2',
}: {
	id?: string;
	displayContext?: CurrencyFormatterDisplayContext;
	totalPrice: PriceOrError | undefined | null;
	pr7?: string | undefined;
	monthlyRateOverride?: number | undefined | null;
	monthlyRateDialog?: boolean;
	monthlyRateDialogReference?: string;
	displayMode?: 'rows' | 'cols';
	vatConfig: VATConfig;
	priceDisclaimer?: { id: string; text: string };
	size?: '1' | '2' | '3';
}) {
	const { i18n } = useI18n();
	const { partRateByPr } = useFinancing();
	const { formatMonthlyRate, formatCurrency } = useCurrencyFormatter();
	const { config } = useMarketConfig();
	const {
		featureServices: { footnoteService },
	} = useAppServices();
	const monthlyRate = monthlyRateOverride ?? partRateByPr(pr7);
	const disclaimerText = useAtomValue(state.financingDisclaimerText);

	if (priceDisclaimer) {
		const footnoteKey = `price-${priceDisclaimer.id}`;
		const footnote = [{ Key: footnoteKey, Text: priceDisclaimer.text ?? '' } as Footnote];
		const alreadyExistingFootnotes = footnoteService?.getData();

		const footnoteFound = alreadyExistingFootnotes?.find((d) => d.Key === footnoteKey);
		if (footnoteFound) {
			footnoteService?.replaceFootNotes(footnote);
		} else {
			footnoteService?.addFootNotes(footnote);
		}
	}

	return (
		<>
			{/* eslint-disable jsx-a11y/no-noninteractive-tabindex */}
			<div tabIndex={0} id={id} className="price-month-rate flex flex-col">
				{config.showPrices && totalPrice?.__typename === 'Price' && typeof totalPrice?.value === 'number' && (
					<Text tag="p" variant={{ type: 'copy', order: size, style: 'normal' }}>
						{formatCurrency(totalPrice, vatConfig, displayContext)}
						{priceDisclaimer?.text && (
							<FootnoteReferenceV2
								footnoteId={`price-${priceDisclaimer.id}`}
								footnoteAriaLabel={i18n('ngc.a11y.footnotes.price.disclaimer')}
							/>
						)}
						&nbsp;
						{!monthlyRateDialog &&
							monthlyRate !== undefined &&
							monthlyRate !== null &&
							monthlyRate !== 0 &&
							!config.hideMonthlyRates && (
								<Text
									tag={displayMode === 'rows' ? 'span' : 'p'}
									variant={{ type: 'copy', order: size, style: 'normal' }}>
									{i18n('ngc.common.or')} {formatMonthlyRate(monthlyRate)}
									<MonthlyRateFootnote text={disclaimerText} />
								</Text>
							)}
						{monthlyRateDialog &&
							monthlyRate !== undefined &&
							monthlyRate !== null &&
							monthlyRate !== 0 &&
							!config.hideMonthlyRates && (
								<Text
									tag={displayMode === 'rows' ? 'span' : 'p'}
									variant={{ type: 'copy', order: size, style: 'normal' }}>
									{i18n('ngc.common.or')} {formatMonthlyRate(monthlyRate)}
									{monthlyRateDialogReference && (
										<FootnoteReferenceNgc
											reference={monthlyRateDialogReference}
											ariaLabel={i18n('ngc.a11y.footnotes.financing')}
										/>
									)}
								</Text>
							)}
					</Text>
				)}
			</div>
		</>
	);
}
